import routes from "../../../../pipeline_api/feedback/routes.json";
// What a feedback pin records about the page: the route, the query and the loan or LO in view.

export interface PageContext {
  route: string;
  /** The query string without `?` and without the `feedback` deep-link parameter. */
  query: string;
  loan_id: string | null;
  core_loan_id: string | null;
  lo_person_id: string | null;
}

/** Shared with API authorization. Unknown/new routes remain private to author/read-all triage. */
export function routeListsLoans(path: string): boolean {
  const route = path.replace(/\/+$/, "").toLowerCase();
  const nonLoan: string[] = routes.non_loan_pages;
  return routes.loan_list_prefixes.some((p) => route === p || route.startsWith(`${p}/`)) || !nonLoan.includes(route);
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function uuid(v: string | null | undefined): string | null {
  return v && UUID.test(v) ? v.toLowerCase() : null;
}

/** The deep-link parameter that opens one pin's thread (`?feedback=<id>`). */
export const FEEDBACK_PARAM = "feedback";

// Cover the whole staff-loan route family, including future nested pages. Path subjects
// take precedence over overlays; the API refuses conflicting contexts rather than leaking either.
const SUBJECT_ROUTES: { pattern: RegExp; field: "loan_id" | "core_loan_id" | "lo_person_id" }[] = [
  { pattern: /^\/loans\/([^/]+)(?:\/.*)?$/i, field: "loan_id" },
  { pattern: /^\/documents\/([^/]+)(?:\/.*)?$/i, field: "core_loan_id" },
  { pattern: /^\/prospecting\/people\/([^/]+)(?:\/.*)?$/i, field: "lo_person_id" },
];

export function pageContext(pathname: string, search: string): PageContext {
  const params = new URLSearchParams(search);
  params.delete(FEEDBACK_PARAM);
  // The router matches case-insensitively and ignores trailing slashes: one route key per screen.
  const route = (pathname || "/").replace(/\/+$/, "").toLowerCase() || "/";
  const ctx: PageContext = { route, query: params.toString(), loan_id: null, core_loan_id: null, lo_person_id: null };
  for (const { pattern, field } of SUBJECT_ROUTES) {
    const match = pattern.exec(pathname);
    if (match) {
      ctx[field] = uuid(match[1]);
      return ctx;
    }
  }
  // The drawer is available over any staff page. Signatures (and the separate desk)
  // use the core_loan query context; preserve it wherever supplied.
  if (uuid(params.get("loan"))) ctx.loan_id = uuid(params.get("loan"));
  else ctx.core_loan_id = uuid(params.get("core_loan"));
  return ctx;
}

/** The URL that opens a pin on its page. */
export function pinHref(pin: { id: string; route: string; query: string }): string {
  const params = new URLSearchParams(pin.query);
  params.set(FEEDBACK_PARAM, pin.id);
  // Stay in the app: a route the API would refuse (`//host`, no leading slash) links to All feedback.
  const route = pin.route.startsWith("/") && !pin.route.startsWith("//") && !pin.route.includes("\\") ? pin.route : "/feedback";
  return `${route}?${params.toString()}`;
}
